/**
 * The row's facts on the onboarding record, under `refs`. One key per
 * template starter, `template:<id>`: the id of the user's copy once a copy
 * started. A second click on that starter opens the copy instead of cloning
 * again. The walkthrough starter is the record's own walkthrough offer.
 */

import {
  TOUR_CANVAS_DESCRIPTION,
  useOnboardingEntry,
  useOnboardingRecord,
  useOnboardingStatus,
  type OnboardingEntry,
} from "@/onboarding";
import type { UserOnboarding } from "@cohesive/api-client";
import { useCallback } from "react";

export { TOUR_CANVAS_DESCRIPTION };

export function templateRef(templateId: string): string {
  return `template:${templateId}`;
}

/** The user's copy of a template, or nothing. */
export function templateCopy(
  record: UserOnboarding | undefined,
  templateId: string,
): string | undefined {
  return record?.refs?.[templateRef(templateId)] || undefined;
}

export interface HomeRecord {
  record: UserOnboarding | undefined;
  /** The record has loaded; until then the row shows no starters. */
  resolved: boolean;
  /** Where the user is with onboarding; "started" is a tour under way. */
  stage: ReturnType<typeof useOnboardingRecord>["stage"];
  walkthrough: OnboardingEntry;
  /** Stamp a template's copy, or `""` to forget one. */
  setTemplateCopy: (templateId: string, canvasId: string) => Promise<boolean>;
  /** Back to a fresh account: no status, no steps, no refs. */
  reset: () => Promise<boolean>;
}

export function useHomeRecord(): HomeRecord {
  const { record, stage } = useOnboardingRecord();
  const status = useOnboardingStatus();
  const walkthrough = useOnboardingEntry();

  const setTemplateCopy = useCallback(
    (templateId: string, canvasId: string) =>
      status.patch({ refs: { [templateRef(templateId)]: canvasId } }),
    [status],
  );
  const reset = useCallback(() => {
    const blank = (keys: Record<string, string> | undefined) =>
      Object.fromEntries(Object.keys(keys ?? {}).map((key) => [key, ""]));
    return status.patch({
      global: "",
      steps: blank(record?.steps),
      refs: blank(record?.refs),
    });
  }, [record, status]);

  return {
    record,
    resolved: stage !== "unresolved",
    stage,
    walkthrough,
    setTemplateCopy,
    reset,
  };
}
